import { useRef, useState } from "react";
import { PolarChart } from "store/slice/charts/chartsSlice.types";

declare module "highcharts" {
    interface Chart {
        spacing: [number, number, number, number];
        margin: [number, number, number, number];
        axisOffset?: number[];
    }

    interface Legend {
        legendWidth?: number;
        legendHeight?: number;
    }
}
interface IUseChartResize {
    chartExtraOptions: React.MutableRefObject<Highcharts.Options | undefined>;
    resizeReRender: number;
    hasChartReArranged: boolean;
    onChartResize: (chart: Highcharts.Chart) => void;
}

const useChartResize = (width: number, height: number, chartId?: string): IUseChartResize => {
    const [resizeReRender, setResizeReRender] = useState<number>(0);
    const chartExtraOptions = useRef<Highcharts.Options | undefined>();
    const hasChartReArranged = useRef(false);

    const onChartResize = (chart: Highcharts.Chart) => {
        const {
            credits,
            plotTop,
            axisOffset,
            fullscreen: { isOpen: isInFullScreen },
            legend: {
                legendWidth,
                legendHeight,
                options: { margin: legendMargin },
            },
        } = chart;

        if (isInFullScreen) {
            width = window.innerWidth;
            height = window.innerHeight - 10;
        }

        const X_AXIS_OFFSET = axisOffset?.length ? axisOffset[2] : 0;
        const LEGEND_WIDTH = legendWidth || 0;
        const LEGEND_HEIGHT = legendHeight || 0;
        const LEGEND_MARGIN = legendMargin || 0;
        const CREDITS = credits?.getBBox();

        const MIN_DIMENSIONS_DIFF = Math.min(width, height) / Math.max(width, height);

        const DIMENSIONS_DIFF = width / height;
        const IS_MIN_DIMENSIONS_DIFF =
            chartId === "behavioural" ? width / height <= 1.5 : DIMENSIONS_DIFF <= MIN_DIMENSIONS_DIFF;

        let newWidth = width;
        let newHeight = height - CREDITS.height;

        if (IS_MIN_DIMENSIONS_DIFF) {
            const clipBoxHeight = height - LEGEND_HEIGHT - X_AXIS_OFFSET - LEGEND_MARGIN;
            newWidth = Math.min(clipBoxHeight, width);
            newHeight =
                width > clipBoxHeight
                    ? newHeight
                    : width + LEGEND_HEIGHT + X_AXIS_OFFSET + LEGEND_MARGIN - CREDITS.height;
        } else {
            newWidth = width - Math.abs(width - height - LEGEND_WIDTH - LEGEND_MARGIN);
            newHeight = newWidth - LEGEND_WIDTH - LEGEND_MARGIN + X_AXIS_OFFSET - CREDITS.height;
        }

        let result: Highcharts.Options = {
            chart: {
                width: newWidth,
                height: newHeight,
            },
            credits: {
                position: {
                    x: IS_MIN_DIMENSIONS_DIFF
                        ? newWidth / 2 - CREDITS.width / 2
                        : (newWidth - LEGEND_WIDTH - LEGEND_MARGIN) / 2 - CREDITS.width / 2,
                    y: IS_MIN_DIMENSIONS_DIFF ? -LEGEND_HEIGHT - LEGEND_MARGIN : -1,
                },
            },
            legend: {
                align: IS_MIN_DIMENSIONS_DIFF ? "center" : "right",
                layout: IS_MIN_DIMENSIONS_DIFF ? "horizontal" : "vertical",
                maxHeight: IS_MIN_DIMENSIONS_DIFF ? 84 : undefined,
            },
        };

        // Tweaks for Balance
        if (chartId?.includes("balance")) {
            result = {
                ...result,
                chart: {
                    ...result.chart,
                    spacing: IS_MIN_DIMENSIONS_DIFF ? [46, 46, 46, 46] : [38, 38, 38, 38],
                    marginRight: IS_MIN_DIMENSIONS_DIFF ? undefined : 75 + LEGEND_WIDTH + LEGEND_MARGIN,
                    marginBottom: IS_MIN_DIMENSIONS_DIFF ? LEGEND_HEIGHT + LEGEND_MARGIN + 38 : undefined,
                },
                credits: {
                    position: {
                        x: IS_MIN_DIMENSIONS_DIFF
                            ? newWidth / 2 - CREDITS.width / 2
                            : (newWidth - LEGEND_WIDTH - LEGEND_MARGIN - 38) / 2 - CREDITS.width / 2,
                        y: IS_MIN_DIMENSIONS_DIFF ? -LEGEND_HEIGHT - LEGEND_MARGIN - CREDITS.height : -20,
                    },
                },
                legend: {
                    ...result.legend,
                    y: IS_MIN_DIMENSIONS_DIFF ? 46 : 0,
                },
            };
        }

        // Tweaks for TeamsCompetitivePositioning and TeamsCompetitiveComparison
        if (chartId?.includes("teamsCompetitive") || chartId === "potential") {
            result = {
                ...result,
                chart: {
                    ...result.chart,
                    spacing: [46, 46, 46, 46],
                    marginRight: IS_MIN_DIMENSIONS_DIFF ? undefined : 75 + LEGEND_WIDTH + LEGEND_MARGIN,
                },
                credits: {
                    position: {
                        x: IS_MIN_DIMENSIONS_DIFF
                            ? newWidth / 2 - CREDITS.width / 2
                            : (newWidth - LEGEND_WIDTH - LEGEND_MARGIN - 38) / 2 - CREDITS.width / 2,
                        y: IS_MIN_DIMENSIONS_DIFF ? -(LEGEND_HEIGHT + LEGEND_MARGIN + plotTop / 2) : -plotTop / 2,
                    },
                },
                legend: {
                    ...result.legend,
                    margin: IS_MIN_DIMENSIONS_DIFF ? 50 : 0,
                },
            };
        }

        // Tweaks for Spiderweb
        if (chartId === "spiderweb") {
            const { pane } = chart as unknown as PolarChart;
            const [, paneY, diameter] = pane[0].center;

            const isSpaceWider = width >= height * 1.5;

            if (isSpaceWider) {
                result = {
                    ...result,
                    chart: {
                        width,
                        height,
                    },
                    pane: {
                        size: "75%",
                        center: ["35%", "50%"],
                    },
                    credits: {
                        position: {
                            ...result.credits?.position,
                            y: IS_MIN_DIMENSIONS_DIFF ? -(LEGEND_HEIGHT + LEGEND_MARGIN + plotTop / 2) : -plotTop / 2,
                        },
                    },
                    legend: {
                        ...result.legend,
                        margin: IS_MIN_DIMENSIONS_DIFF ? 50 : 0,
                        y: -paneY + diameter / 2 + CREDITS.height * 2,
                    },
                };
            } else {
                result = {
                    ...result,
                    chart: {
                        width,
                        height,
                    },
                    pane: {
                        size: "72%",
                        center: ["50%", "42%"],
                    },
                    credits: {
                        position: {
                            x: newWidth / 2 - CREDITS.width / 2,
                            y: 0,
                        },
                    },
                    legend: {
                        align: "center",
                        layout: "horizontal",
                        y: -paneY + diameter / 2 + CREDITS.height * 2,
                    },
                };
            }
        }

        // Tweaks for Behavioural
        if (chartId === "behavioural") {
            result = {
                ...result,
                chart: {
                    width: IS_MIN_DIMENSIONS_DIFF ? width * 0.85 : newWidth * 0.95,
                    height: newHeight * 0.95,
                },
                credits: {
                    position: {
                        x: IS_MIN_DIMENSIONS_DIFF
                            ? (width * 0.85) / 2 - CREDITS.width / 2
                            : (newWidth * 0.95) / 2 - CREDITS.width / 2,
                        y: -2,
                    },
                },
                legend: {
                    align: DIMENSIONS_DIFF < 1.5 ? "center" : "right",
                    layout: DIMENSIONS_DIFF < 1.5 ? "horizontal" : "vertical",
                    maxHeight: DIMENSIONS_DIFF < 1.5 ? 70 : undefined,
                },
            };
        }

        // Tweaks for Bridge
        if (chartId === "waterfall") {
            const min = Math.min(width, height);
            const max = Math.max(width, height);

            const newWidth = min * (width < height ? 1 : max / min);

            result = {
                ...result,
                chart: {
                    width: width,
                    height: Math.min(height, newWidth * 0.45),
                },
                credits: {
                    position: {
                        x: IS_MIN_DIMENSIONS_DIFF
                            ? (newWidth * 1.15) / 2 - CREDITS.width
                            : (newWidth - LEGEND_WIDTH - LEGEND_MARGIN) / 2 - CREDITS.width / 2,
                        y: -2,
                    },
                },
            };
        }

        hasChartReArranged.current = IS_MIN_DIMENSIONS_DIFF;
        chartExtraOptions.current = result;
        setResizeReRender(newWidth || newHeight);
    };

    return { chartExtraOptions, resizeReRender, hasChartReArranged: hasChartReArranged.current, onChartResize };
};

export default useChartResize;
